/**
 * React Query hook for stats data using Orval-generated client
 */
import { useQuery } from '@tanstack/react-query';
import { eventsControllerGetStats } from '@/generated/api/events/events';
import type { EventsControllerGetStatsParams } from '@/generated/api/model';
import type { StatsResponse } from '@/shared/types';
import { mockStats } from '@/shared/api/mock';
import { config } from '@/shared/utils/config';

/**
 * Parse amount string to number (API returns amounts as strings)
 */
function parseAmount(amount: string | number | null | undefined): number {
  if (amount == null) return 0;
  if (typeof amount === 'number') return amount;
  const parsed = parseFloat(amount);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Transform backend response to frontend format
 * CRITICAL: API returns summary.totalAmount as STRING (e.g., "47.295600")
 * We must parse it to number for proper display and calculations
 */
function transformStatsResponse(backendData: any): StatsResponse {
  const summary = backendData?.summary || {};
  const byEvent = backendData?.byEvent || [];

  // Parse totalAmount from string to number (SOURCE OF TRUTH for revenue)
  const totalRevenue = parseAmount(summary.totalAmount);
  const currency = summary.currency || 'EUR';

  // Debug logging (can be removed or guarded by env var)
  if (import.meta.env.DEV) {
    console.debug('[Stats] API Response:', {
      totalAmount: summary.totalAmount,
      totalAmountType: typeof summary.totalAmount,
      parsedAmount: totalRevenue,
      currency,
      totalFtds: summary.totalFtds,
    });
  }

  // Extract FTD-specific amount from byEvent if available
  const ftdEvent = byEvent.find((e: any) => e.event === 'FTD');
  const ftdSpecificAmount = ftdEvent?.totalAmount !== undefined
    ? parseAmount(ftdEvent.totalAmount)
    : null;

  // Use FTD-specific amount if available, otherwise use total revenue
  // This allows showing FTD amount when byEvent has it, or total deposits otherwise
  const amountSum = ftdSpecificAmount ?? totalRevenue;

  return {
    summary: {
      totalClicks: summary.totalClicks ?? 0,
      totalRegs: summary.totalRegs ?? 0,
      totalFtds: summary.totalFtds ?? 0,
      totalRedeps: summary.totalRedeps ?? 0,
      totalDeps: summary.totalDeps ?? 0,
      totalCpas: summary.totalCpas ?? 0,
      totalAmount: summary.totalAmount, // Keep as string from API
      amountSum, // Parsed number for backward compatibility
      currency: summary.currency || 'EUR',
      click2reg: summary.click2reg ?? 0,
      reg2ftd: summary.reg2ftd ?? 0,
      reg2cpa: summary.reg2cpa ?? 0,
    },
    timeSeries: backendData?.timeSeries?.map((point: any) => ({
      period: point.period,
      count: point.count ?? 0,
      clicks: point.clicks ?? 0,
      regs: point.regs ?? 0,
      ftds: point.ftds ?? 0,
      cpas: point.cpas ?? 0,
      totalAmount: point.totalAmount,
    })),
    breakdown: backendData?.breakdown,
  };
}

/**
 * Hook to fetch statistics with Orval-generated API
 */
export function useStats(
  queryParams: EventsControllerGetStatsParams | null,
  options?: { enabled?: boolean }
) {
  return useQuery<StatsResponse>({
    queryKey: ['stats', queryParams],
    queryFn: async () => {
      if (!queryParams) {
        throw new Error('Query params are required');
      }
      if (config.mockMode) {
        return mockStats.getStats();
      }
      // Orval wraps response, extract data if needed
      const response = await eventsControllerGetStats(queryParams);
      const data = typeof response === 'object' && response && 'data' in response ? response.data : response;

      // Transform backend response to match frontend types
      return transformStatsResponse(data);
    },
    staleTime: 30000, // 30 seconds
    enabled: options?.enabled ?? queryParams !== null,
  });
}
